/** The phone's rooms: a room as one line, the All rooms screen, and a room's own screen with its places. */
import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import type { HostRoomRow, TalkRoomKind } from "../../shared/talk";
import { Icon } from "../components/ui/Icon";
import { Toggle } from "../components/ui/Toggle";
import { Poster } from "./parts";
import { Nav, newCount, plainCount, Row } from "./phone";
import { announcementsPath, circlePath, everyonePath, filesPath, placesPath, questionsPath, roomHome, talkPath } from "./places";
import { useKeepRoom } from "./RailRooms";
import { allRoomsView, kindLine, lastTalkWords, roomLive, roomNew } from "./rooms";
import { useTalkContext } from "./TalkLayout";

/** A room as one line: its picture, its name, what is new. It opens the room's own screen. */
export function RoomLine({ room }: { room: HostRoomRow }) {
  const navigate = useNavigate();
  const fresh = roomNew(room);
  return (
    <button type="button" className="hp-roomline" onClick={() => navigate(placesPath(room.webinarId))}>
      <Poster url={room.posterUrl} />
      <span className="hp-roomline-t">
        <b>{room.title}</b>
        <small>{kindLine(room)}</small>
      </span>
      {roomLive(room) ? <span className="ch-live">Live</span> : fresh ? newCount(fresh) : <small className="hp-c">{lastTalkWords(room)}</small>}
      <Icon name="chev" />
    </button>
  );
}

/** Every room, its own pushed screen: find, the kinds, the order. */
export function AllRoomsScreen() {
  const { rail, lay } = useTalkContext();
  const [find, setFind] = useState("");
  const [kind, setKind] = useState<TalkRoomKind | "all">("all");
  const [order, setOrder] = useState<"recent" | "az">("recent");
  if (lay !== "phone") return <Navigate to={talkPath} replace />;
  const rooms = rail?.rooms ?? [];
  const view = allRoomsView(rooms, find, kind, order);
  const named = view.kinds.reduce((n, k) => n + k.count, 0);
  return (
    <div className="hp">
      <Nav back="Rooms" title="All rooms" sub={`${rooms.length} rooms`} />
      <div className="hp-scroll">
        <div className="hp-page">
          <label className="hp-q in">
            <Icon name="search" />
            <input aria-label="Find a room" placeholder="Find a room" value={find} onChange={(e) => setFind(e.target.value)} />
          </label>
          <div className="hp-chips">
            <button type="button" className={kind === "all" ? "fchip on" : "fchip"} onClick={() => setKind("all")}>
              All {named}
            </button>
            {view.kinds.map((k) => (
              <button key={k.kind} type="button" className={kind === k.kind ? "fchip on" : "fchip"} onClick={() => setKind(k.kind)}>
                {k.kind} {k.count}
              </button>
            ))}
          </div>
          <div className="gseg hp-order" role="tablist" aria-label="Order">
            <button type="button" className={order === "recent" ? "on" : undefined} onClick={() => setOrder("recent")}>
              Latest talk
            </button>
            <button type="button" className={order === "az" ? "on" : undefined} onClick={() => setOrder("az")}>
              A to Z
            </button>
          </div>
          {view.shown.length ? (
            <section className="hp-card">
              {view.shown.map((r) => (
                <RoomLine key={r.webinarId} room={r} />
              ))}
            </section>
          ) : (
            <p className="empty-note">No room has that name.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/** A room's own screen on a phone: its places, its circles, Keep at the top, and the room's page. On a wider screen it opens the room. */
export function PlacesScreen() {
  const { wid = "" } = useParams();
  const { rail, lay, startCircle, reloadRail } = useTalkContext();
  const navigate = useNavigate();
  const keep = useKeepRoom(reloadRail);
  const room = rail?.rooms.find((r) => r.webinarId === wid) ?? null;
  const [on, setOn] = useState<boolean | null>(null);
  if (!rail) return <div className="hp" />;
  if (!room) return <Navigate to={talkPath} replace />;
  if (lay !== "phone") return <Navigate to={roomHome(room)} replace />;
  const kept = on ?? room.kept;
  return (
    <div className="hp">
      <Nav back="Rooms" title={room.title} sub={kindLine(room)} />
      <div className="hp-scroll">
        <div className="hp-page">
          <section className="hp-card">
            {room.questionsWaiting !== null ? <Row icon="chat" label="Questions" end={room.questionsWaiting ? newCount(room.questionsWaiting) : undefined} onClick={() => navigate(questionsPath(wid))} /> : null}
            {room.everyoneChat ? <Row icon="users" label="Everyone" end={room.everyoneNew ? newCount(room.everyoneNew) : undefined} onClick={() => navigate(everyonePath(wid))} /> : null}
            <Row icon="bell" label="Announcements" onClick={() => navigate(announcementsPath(wid))} />
            <Row icon="fold" label="Files" end={plainCount(room.filesCount)} onClick={() => navigate(filesPath(wid))} />
          </section>
          <p className="hp-sub big">Circles</p>
          <section className="hp-card">
            {room.circles.map((c) => (
              <Row key={c.circleId} icon="target" label={c.name} end={c.live ? <span className="ch-live">Live</span> : c.newCount ? newCount(c.newCount) : plainCount(c.members)} onClick={() => navigate(circlePath(c.circleId))} />
            ))}
            <Row icon="plus" label="Start a circle" accent onClick={() => startCircle(wid)} />
          </section>
          <section className="hp-card hp-sets">
            <div className="set">
              <span>
                <b>Keep at the top</b>
                <small>First in your rooms, with its places</small>
              </span>
              <Toggle
                on={kept}
                label="Keep at the top"
                onChange={(v) => {
                  setOn(v);
                  void keep({ webinarId: wid, kept: !v }).then((saved) => saved || setOn(null));
                }}
              />
            </div>
          </section>
          <section className="hp-card">
            <Row icon="ext" label="The room's page" onClick={() => navigate(`/rooms/${encodeURIComponent(wid)}`)} />
          </section>
        </div>
      </div>
    </div>
  );
}
